import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import {
  authAcceptInviteExistingUserMutation,
  authAcceptInviteNewUserMutation,
  authCheckInviteOptions,
} from '../../../../@api-client/@tanstack/react-query.gen';
import type { InviteAcceptFormData } from './invited-page.types';

/**
 * Shared invite-acceptance data layer. Reads `inviteId` from the route, suspends on the
 * invite check, and exposes both frozen accept paths: new user (email/password) and existing
 * user (one-tap). Callers own navigation and their own copy, including the venue-name fallback.
 */
export const useInviteAccept = () => {
  const { inviteId } = useParams();

  const { data } = useSuspenseQuery({
    ...authCheckInviteOptions({ query: { inviteId: inviteId! } }),
  });

  const { mutateAsync: mutateAsyncNew } = useMutation({ ...authAcceptInviteNewUserMutation() });
  const { mutateAsync: mutateAsyncExisting, isPending: isAcceptingAsExistingUser } = useMutation({
    ...authAcceptInviteExistingUserMutation(),
  });

  return {
    inviteId,
    venueName: data?.data?.venueName,
    accountExists: data.data.accountExists,
    isAcceptingAsExistingUser,
    acceptAsNewUser: (formData: InviteAcceptFormData) =>
      mutateAsyncNew({ query: { inviteId: inviteId! }, body: { ...formData } }),
    acceptAsExistingUser: () => mutateAsyncExisting({ query: { inviteId: inviteId! } }),
  };
};
